/**
 * Controlled vocabularies.
 *
 * Every enum is declared once as a Zod schema and its TypeScript type is inferred
 * from it, so the runtime validator and the compile-time type can never drift.
 * The Python matcher service mirrors these as Pydantic `Literal` unions, and
 * `shared/src/__tests__` asserts the two lists agree.
 */
import { z } from 'zod'

// ---------------------------------------------------------------------------
// Trials
// ---------------------------------------------------------------------------

/**
 * Recruitment status, using ClinicalTrials.gov's own vocabulary so that ingest is
 * a rename rather than a remapping.
 *
 * Only `RECRUITING` and `ENROLLING_BY_INVITATION` can accept a new patient, and
 * `isRecruitingStatus` is the single place that judgement is encoded — several
 * screens need it and they must agree.
 */
export const TrialStatusSchema = z.enum([
  'RECRUITING',
  'NOT_YET_RECRUITING',
  'ENROLLING_BY_INVITATION',
  'ACTIVE_NOT_RECRUITING',
  'SUSPENDED',
  'TERMINATED',
  'COMPLETED',
  'WITHDRAWN',
  'UNKNOWN',
])
export type TrialStatus = z.infer<typeof TrialStatusSchema>

export function isRecruitingStatus(status: TrialStatus): boolean {
  return status === 'RECRUITING' || status === 'ENROLLING_BY_INVITATION'
}

/**
 * Trial phase. `NA` covers observational studies and device trials, which have no
 * phase but are still matchable — excluding them would silently drop a large
 * share of the registry.
 */
export const TrialPhaseSchema = z.enum([
  'EARLY_PHASE1',
  'PHASE1',
  'PHASE1_PHASE2',
  'PHASE2',
  'PHASE2_PHASE3',
  'PHASE3',
  'PHASE4',
  'NA',
])
export type TrialPhase = z.infer<typeof TrialPhaseSchema>

export const StudyTypeSchema = z.enum(['INTERVENTIONAL', 'OBSERVATIONAL', 'EXPANDED_ACCESS'])
export type StudyType = z.infer<typeof StudyTypeSchema>

export const SiteStatusSchema = z.enum(['RECRUITING', 'NOT_YET_RECRUITING', 'ACTIVE_NOT_RECRUITING', 'COMPLETED', 'SUSPENDED', 'WITHDRAWN', 'UNKNOWN'])
export type SiteStatus = z.infer<typeof SiteStatusSchema>

// ---------------------------------------------------------------------------
// Demographics
// ---------------------------------------------------------------------------

/**
 * Sex as used for *eligibility*, which is what trials specify. This is
 * biological/administrative sex, not gender identity, and the two are recorded
 * separately: conflating them produces both wrong eligibility answers and a
 * disrespectful UI. `ALL` is the trial-side value meaning "no sex restriction".
 */
export const SexSchema = z.enum(['FEMALE', 'MALE', 'ALL', 'UNKNOWN'])
export type Sex = z.infer<typeof SexSchema>

/** Gender identity, collected for reporting and respectful address only — never used as a filter. */
export const GenderIdentitySchema = z.enum([
  'WOMAN',
  'MAN',
  'NONBINARY',
  'TRANSGENDER_WOMAN',
  'TRANSGENDER_MAN',
  'OTHER',
  'PREFER_NOT_TO_SAY',
  'UNKNOWN',
])
export type GenderIdentity = z.infer<typeof GenderIdentitySchema>

/** OMB race categories, multi-select. Used exclusively for enrollment-equity reporting. */
export const RaceSchema = z.enum([
  'AMERICAN_INDIAN_OR_ALASKA_NATIVE',
  'ASIAN',
  'BLACK_OR_AFRICAN_AMERICAN',
  'NATIVE_HAWAIIAN_OR_OTHER_PACIFIC_ISLANDER',
  'WHITE',
  'MIDDLE_EASTERN_OR_NORTH_AFRICAN',
  'OTHER',
  'PREFER_NOT_TO_SAY',
  'UNKNOWN',
])
export type Race = z.infer<typeof RaceSchema>

export const EthnicitySchema = z.enum(['HISPANIC_OR_LATINO', 'NOT_HISPANIC_OR_LATINO', 'PREFER_NOT_TO_SAY', 'UNKNOWN'])
export type Ethnicity = z.infer<typeof EthnicitySchema>

// ---------------------------------------------------------------------------
// Eligibility criteria
// ---------------------------------------------------------------------------

export const CriterionTypeSchema = z.enum(['INCLUSION', 'EXCLUSION'])
export type CriterionType = z.infer<typeof CriterionTypeSchema>

/**
 * What kind of fact a criterion asserts. This drives which evaluator handles it,
 * which patient fields it reads, and which UI affordance renders it.
 *
 * The taxonomy is intentionally shallow. A deeper ontology is more faithful to
 * the clinical text but produces buckets too small to learn from, and every
 * additional kind is an evaluator someone has to write and test.
 */
export const CriterionKindSchema = z.enum([
  'AGE', // "18 years or older"
  'SEX', // "female subjects only"
  'CONDITION', // "histologically confirmed stage III melanoma"
  'LAB', // "ANC >= 1500/mm3"
  'MEDICATION', // "currently receiving anticoagulation"
  'PROCEDURE', // "prior hematopoietic stem cell transplant"
  'BIOMARKER', // "EGFR exon 19 deletion"
  'PERFORMANCE_STATUS', // "ECOG 0-1"
  'PRIOR_THERAPY', // "at least one prior line of platinum-based chemotherapy"
  'PREGNANCY', // "not pregnant or breastfeeding"
  'COMORBIDITY', // "no uncontrolled hypertension"
  'TEMPORAL', // "diagnosed within the last 6 months"
  'CONSENT_CAPACITY', // "able to provide written informed consent"
  'LOGISTICAL', // "willing to attend weekly visits"
  'INVESTIGATOR_DISCRETION', // "in the opinion of the investigator, ..."
  'OTHER',
])
export type CriterionKind = z.infer<typeof CriterionKindSchema>

/**
 * Kinds that no algorithm should claim to decide.
 *
 * `INVESTIGATOR_DISCRETION` is unfalsifiable by construction; `LOGISTICAL` and
 * `CONSENT_CAPACITY` depend on facts that live in a conversation, not a chart.
 * The evaluator returns `INSUFFICIENT_INFORMATION` for these and the UI marks
 * them "needs a human" — which is more useful than a confident guess, because it
 * tells the coordinator exactly which questions the visit has to answer.
 */
export const HUMAN_ONLY_CRITERION_KINDS: readonly CriterionKind[] = [
  'INVESTIGATOR_DISCRETION',
  'LOGISTICAL',
  'CONSENT_CAPACITY',
]

export function isHumanOnlyKind(kind: CriterionKind): boolean {
  return HUMAN_ONLY_CRITERION_KINDS.includes(kind)
}

/** Numeric comparator for threshold criteria (`LAB`, `AGE`, `PERFORMANCE_STATUS`). */
export const ComparatorSchema = z.enum(['GT', 'GTE', 'LT', 'LTE', 'EQ', 'NEQ', 'BETWEEN'])
export type Comparator = z.infer<typeof ComparatorSchema>

/** Coding systems we normalize into. See `codes.ts` for the crosswalk helpers. */
export const CodeSystemSchema = z.enum([
  'SNOMEDCT',
  'ICD10CM',
  'LOINC',
  'RXNORM',
  'ATC',
  'CPT',
  'HCPCS',
  'HGNC',
  'HGVS',
  'ONCOTREE',
  'NCIT',
  'MEDDRA',
  'UMLS',
  'LOCAL', // our own vocabulary, used by the synthetic data generator
])
export type CodeSystem = z.infer<typeof CodeSystemSchema>

// ---------------------------------------------------------------------------
// Matching output
// ---------------------------------------------------------------------------

/**
 * Verdict for a single criterion.
 *
 * `INSUFFICIENT_INFORMATION` is not a failure mode — it is the most common honest
 * answer and the most actionable one, because it names a specific missing lab or
 * note. Collapsing it into `NOT_MET` would silently convert "we don't know" into
 * "no", which is exactly the invisible false exclusion this system is built to
 * avoid.
 */
export const VerdictSchema = z.enum(['MET', 'NOT_MET', 'INSUFFICIENT_INFORMATION', 'NOT_APPLICABLE'])
export type Verdict = z.infer<typeof VerdictSchema>

/**
 * Qualitative band shown instead of a raw probability where a number would imply
 * false precision — notably the entire patient-facing view.
 */
export const MatchBandSchema = z.enum(['LIKELY_FIT', 'POSSIBLE_FIT', 'UNLIKELY_FIT', 'NOT_ELIGIBLE'])
export type MatchBand = z.infer<typeof MatchBandSchema>

/**
 * Band cut-points for the calibrated eligibility probability.
 *
 * These are product decisions, not statistical ones, and they are asymmetric on
 * purpose: the floor for `POSSIBLE_FIT` is low (0.20) because the cost of a
 * coordinator glancing at a marginal candidate is far below the cost of never
 * surfacing an eligible patient. Any hard blocking exclusion overrides the
 * probability entirely — see `bandFor`.
 */
export const MATCH_BAND_THRESHOLDS = { LIKELY_FIT: 0.65, POSSIBLE_FIT: 0.2 } as const

export function bandFor(probability: number, hasBlockingExclusion: boolean): MatchBand {
  if (hasBlockingExclusion) return 'NOT_ELIGIBLE'
  if (probability >= MATCH_BAND_THRESHOLDS.LIKELY_FIT) return 'LIKELY_FIT'
  if (probability >= MATCH_BAND_THRESHOLDS.POSSIBLE_FIT) return 'POSSIBLE_FIT'
  return 'UNLIKELY_FIT'
}

/** Plain-language band labels for the patient view. Deliberately hedged, never advisory. */
export const PATIENT_BAND_COPY: Record<MatchBand, { label: string; detail: string }> = {
  LIKELY_FIT: {
    label: 'Worth asking about',
    detail: 'Based on what we know, you may meet the main requirements. A study nurse would need to confirm.',
  },
  POSSIBLE_FIT: {
    label: 'Might be possible',
    detail: 'Some requirements look like a fit and some we could not check from your records.',
  },
  UNLIKELY_FIT: {
    label: 'Probably not a fit',
    detail: 'Several requirements do not look like a match, but you can still ask your doctor.',
  },
  NOT_ELIGIBLE: {
    label: 'Not a fit right now',
    detail: 'This study has a requirement your records do not meet. Requirements can change over time.',
  },
}

// ---------------------------------------------------------------------------
// Candidate lifecycle
// ---------------------------------------------------------------------------

/**
 * The referral funnel, from "an algorithm noticed" to a terminal outcome.
 *
 * This state machine is the product's most valuable data asset: each transition
 * is a human telling us the model was right or wrong, at a specific criterion,
 * with a reason. `SCREEN_FAILED` with a `screenFailCriterionId` is a labelled
 * training example that no public dataset can give us.
 */
export const CandidateStateSchema = z.enum([
  'SURFACED', // the matcher scored it; no human has looked
  'SHORTLISTED', // a coordinator or clinician flagged it for review
  'DISMISSED', // a human rejected it before any contact — carries a reason
  'CLINICIAN_REVIEWED', // the treating clinician confirmed it is worth raising
  'OUTREACH_APPROVED', // approved for contact under an IRB-approved script
  'PATIENT_INTERESTED', // the patient consented to be contacted / expressed interest
  'PATIENT_DECLINED', // the patient said no
  'PRESCREENED', // chart review or phone screen passed
  'SCREENING_SCHEDULED',
  'CONSENTED', // signed informed consent
  'ENROLLED', // terminal success
  'SCREEN_FAILED', // terminal failure at the screening visit — the richest label
  'WITHDRAWN', // terminal, patient-initiated after enrollment
])
export type CandidateState = z.infer<typeof CandidateStateSchema>

/** Terminal states. No transition leaves these except an explicit admin correction. */
export const TERMINAL_CANDIDATE_STATES: readonly CandidateState[] = [
  'ENROLLED',
  'SCREEN_FAILED',
  'WITHDRAWN',
  'PATIENT_DECLINED',
  'DISMISSED',
]

/**
 * Legal transitions. Enforced in Cloud Functions, not in the UI, so that a stale
 * browser tab or a replayed request cannot drive a candidate into a state that
 * skipped consent.
 *
 * Note that `OUTREACH_APPROVED → PATIENT_INTERESTED` is the only path to patient
 * contact: there is no edge from `SURFACED` straight to `PRESCREENED`. That gap
 * is deliberate and reflects the HIPAA "reviews preparatory to research"
 * boundary, where identifying a candidate and recruiting them are different acts
 * with different permissions.
 */
export const CANDIDATE_TRANSITIONS: Record<CandidateState, readonly CandidateState[]> = {
  SURFACED: ['SHORTLISTED', 'DISMISSED'],
  SHORTLISTED: ['CLINICIAN_REVIEWED', 'DISMISSED'],
  DISMISSED: [],
  CLINICIAN_REVIEWED: ['OUTREACH_APPROVED', 'DISMISSED'],
  OUTREACH_APPROVED: ['PATIENT_INTERESTED', 'PATIENT_DECLINED'],
  PATIENT_INTERESTED: ['PRESCREENED', 'PATIENT_DECLINED'],
  PATIENT_DECLINED: [],
  PRESCREENED: ['SCREENING_SCHEDULED', 'SCREEN_FAILED', 'DISMISSED'],
  SCREENING_SCHEDULED: ['CONSENTED', 'SCREEN_FAILED', 'PATIENT_DECLINED'],
  CONSENTED: ['ENROLLED', 'SCREEN_FAILED', 'WITHDRAWN'],
  ENROLLED: ['WITHDRAWN'],
  SCREEN_FAILED: [],
  WITHDRAWN: [],
}

export function canTransition(from: CandidateState, to: CandidateState): boolean {
  return CANDIDATE_TRANSITIONS[from].includes(to)
}

export function isTerminalState(state: CandidateState): boolean {
  return TERMINAL_CANDIDATE_STATES.includes(state)
}

/** Ordered funnel stages for the sponsor's pipeline chart. Terminal failures are excluded. */
export const FUNNEL_ORDER: readonly CandidateState[] = [
  'SURFACED',
  'SHORTLISTED',
  'CLINICIAN_REVIEWED',
  'OUTREACH_APPROVED',
  'PATIENT_INTERESTED',
  'PRESCREENED',
  'SCREENING_SCHEDULED',
  'CONSENTED',
  'ENROLLED',
]

// ---------------------------------------------------------------------------
// Identity, roles, access
// ---------------------------------------------------------------------------

export const OrgTypeSchema = z.enum(['SPONSOR', 'CRO', 'HEALTH_SYSTEM', 'ACADEMIC_CENTER', 'REGISTRY'])
export type OrgType = z.infer<typeof OrgTypeSchema>

/**
 * Roles, which map to the three views the product owner asked for plus the
 * permission split inside the trial-organization view (see `DECISIONS.md` D-02).
 *
 * `ORG_ANALYST` exists because feasibility analysts need aggregate counts across
 * every site but must never reach a patient record — the least-privilege split
 * that makes the sponsor view safe to give to a whole clinical-operations team.
 */
export const RoleSchema = z.enum([
  'PATIENT',
  'CLINICIAN', // treating/referring physician — sees only their own panel
  'INVESTIGATOR', // PI/sub-I on a trial — sees their site's candidates
  'ORG_COORDINATOR', // CRC: works the candidate pipeline for their site
  'ORG_ANALYST', // feasibility: aggregate counts only, never identifiers
  'ORG_ADMIN', // manages a sponsor/CRO org and its trials
  'PLATFORM_ADMIN',
])
export type Role = z.infer<typeof RoleSchema>

/** Which top-level view a role lands in after sign-in. */
export const ROLE_HOME: Record<Role, '/patient' | '/clinician' | '/org' | '/admin'> = {
  PATIENT: '/patient',
  CLINICIAN: '/clinician',
  INVESTIGATOR: '/clinician',
  ORG_COORDINATOR: '/org',
  ORG_ANALYST: '/org',
  ORG_ADMIN: '/org',
  // No separate admin area exists; the platform operator works in the org views.
  PLATFORM_ADMIN: '/org',
}

/**
 * Purpose-of-use, recorded on every audit event.
 *
 * This is not decoration. Under the HIPAA Privacy Rule the *same* read of the
 * *same* record can be permitted or prohibited depending on why it happened:
 * `RESEARCH_PREPARATORY` permits identifying a potential candidate but does not
 * permit contacting them, whereas `RESEARCH_RECRUITMENT` requires an IRB waiver
 * or authorization. Storing the purpose is what makes the log auditable at all.
 */
export const PurposeOfUseSchema = z.enum([
  'TREATMENT', // a clinician viewing their own patient
  'RESEARCH_PREPARATORY', // identifying candidates; contact NOT permitted
  'RESEARCH_RECRUITMENT', // contacting candidates; requires waiver/authorization
  'PATIENT_SELF_ACCESS', // a patient reading their own record
  'OPERATIONS', // support/debugging, break-glass
  'MODEL_EVALUATION', // reading records to measure the model
])
export type PurposeOfUse = z.infer<typeof PurposeOfUseSchema>

export const AuditActionSchema = z.enum([
  'PATIENT_RECORD_VIEW',
  'PATIENT_SEARCH',
  'MATCH_RUN',
  'MATCH_VIEW',
  'CANDIDATE_STATE_CHANGE',
  'COHORT_QUERY_RUN',
  'CONSENT_CHANGE',
  'OUTREACH_SEND',
  'EXPORT',
  'ROLE_CHANGE',
  'BREAK_GLASS',
  'LOGIN',
])
export type AuditAction = z.infer<typeof AuditActionSchema>

// ---------------------------------------------------------------------------
// Consent
// ---------------------------------------------------------------------------

/**
 * Consent is scoped, not global. A patient agreeing to appear in de-identified
 * feasibility counts has not agreed to be phoned, and the system must be able to
 * tell those apart.
 */
export const ConsentScopeSchema = z.enum([
  'PLATFORM_TERMS', // account terms
  'RECORD_ANALYSIS', // may run matching over my record
  'DEIDENTIFIED_FEASIBILITY', // may include me in aggregate counts shown to sponsors
  'CONTACT_BY_CARE_TEAM', // my own clinician may raise trials with me
  'CONTACT_BY_STUDY_TEAM', // a study team at another org may contact me
  'RESEARCH_DATA_SHARING', // may share my record with a study team after interest
])
export type ConsentScope = z.infer<typeof ConsentScopeSchema>

export const ConsentStateSchema = z.enum(['GRANTED', 'DENIED', 'REVOKED', 'EXPIRED', 'NOT_ASKED'])
export type ConsentState = z.infer<typeof ConsentStateSchema>

/**
 * Consent scopes that must be `GRANTED` before the matcher may run at all.
 * Checked server-side in the match orchestration function.
 */
export const CONSENT_REQUIRED_TO_MATCH: readonly ConsentScope[] = ['RECORD_ANALYSIS']

/** Consent scopes required before any outreach state transition is permitted. */
export const CONSENT_REQUIRED_TO_CONTACT: readonly ConsentScope[] = ['CONTACT_BY_STUDY_TEAM']

// ---------------------------------------------------------------------------
// Provenance
// ---------------------------------------------------------------------------

/**
 * Where a clinical fact came from. Surfaced in the evidence table, because a
 * coordinator's trust in "hemoglobin 9.1" depends entirely on whether it came
 * from a structured lab feed or was pulled out of a narrative note by a parser.
 */
export const DataSourceTypeSchema = z.enum([
  'STRUCTURED_EHR', // a coded observation from a FHIR/HL7 feed
  'CLINICAL_NOTE', // extracted from narrative text
  'PATIENT_REPORTED', // the patient told us
  'CLAIMS',
  'GENOMIC_REPORT',
  'SYNTHETIC', // generated by our data generator — every patient in this repo
  'INFERRED', // derived by us from other facts (e.g. a comorbidity index)
  'CLINICIAN_ENTERED', // typed directly into this app by a member of the care team
])
export type DataSourceType = z.infer<typeof DataSourceTypeSchema>

/**
 * How much a criterion evaluation should be trusted given its source.
 * Used as a reranker feature and to order the evidence table so that the
 * weakest evidence is the most visible.
 */
export const SOURCE_RELIABILITY: Record<DataSourceType, number> = {
  STRUCTURED_EHR: 1.0,
  GENOMIC_REPORT: 1.0,
  CLAIMS: 0.8,
  CLINICAL_NOTE: 0.7,
  PATIENT_REPORTED: 0.6,
  INFERRED: 0.6,
  SYNTHETIC: 0.9,
  CLINICIAN_ENTERED: 0.9,
}
